// The consumer's photos on a case (migrations 043/045): square tiles from the 480 px copies, and a
// viewer that opens the original full size, with Previous and Next (arrow keys) and Esc to close.
// Both are read from our own Blob copy through the API, never from the form's file host.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { officeFocus } from '../../shells/OfficeShell';
import { PhotoImage } from './LinePhotos';

export const complaintPhotoPath = (caseId: number, photoId: number, thumb = false) => `/complaints/${caseId}/photos/${photoId}${thumb ? '?size=thumb' : ''}`;

export function ComplaintPhotoGrid({ caseId, caseNo, photos }: { caseId: number; caseNo: string; photos: Array<{ photoId: number }> }) {
  const [open, setOpen] = useState<number | null>(null);
  const n = photos.length;
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setOpen((i) => (i === null ? i : (i + 1) % n));
      if (e.key === 'ArrowLeft') setOpen((i) => (i === null ? i : (i - 1 + n) % n));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, n]);
  if (n === 0) return null;
  const current = open === null ? null : photos[open];
  return (
    <>
      <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-(--s2) p-0">
        {photos.map((p, i) => (
          <li key={p.photoId}>
            <button type="button" className={`block w-full rounded-(--radius-sm) ${officeFocus}`} aria-label={`Open photo ${i + 1} of ${n} full size`} onClick={() => setOpen(i)}>
              <PhotoImage path={complaintPhotoPath(caseId, p.photoId, true)} alt={`${caseNo}: the consumer's photo ${i + 1}`} size="thumb" className="aspect-square w-full" interactive={false} />
            </button>
          </li>
        ))}
      </ul>
      <Sheet open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent side="right" className="flex w-full flex-col gap-(--s3) overflow-y-auto overscroll-contain sm:max-w-[min(960px,94vw)]">
          <SheetHeader>
            <SheetTitle>
              {caseNo} · photo {open === null ? '' : open + 1} of {n}
            </SheetTitle>
            <SheetDescription>The consumer's photo as they sent it, from the app's own copy.</SheetDescription>
          </SheetHeader>
          {current ? <PhotoImage key={current.photoId} path={complaintPhotoPath(caseId, current.photoId)} alt={`${caseNo}: the consumer's photo ${open! + 1}`} size="large" className="max-h-[75vh] w-full" /> : null}
          {n > 1 ? (
            <div className="flex justify-between gap-(--s2)">
              <Button type="button" variant="outline" onClick={() => setOpen((i) => (i === null ? i : (i - 1 + n) % n))}>
                Previous
              </Button>
              <Button type="button" variant="outline" onClick={() => setOpen((i) => (i === null ? i : (i + 1) % n))}>
                Next
              </Button>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
